import dayjs, { type Dayjs } from "dayjs";

export type DriverStateKey =
  | "DRIVING"
  | "DRIVING_WARNING"
  | "DRIVING_VIOLATION"
  | "BREAK"
  | "REST"
  | "AVAILABLE"
  | "WORK"
  | "NOT_AVAILABLE";

export interface ApiDriverActivity {
  workingState?: string;
  stateType?: string;
  startTime?: string;
  endTime?: string;
  duration?: string;
  dddStatus?: boolean;
}

export interface ApiActivityDay {
  dateAndMonth?: string;
  commitment?: string;
  activities?: ApiDriverActivity[];
}

export interface ActivityResponse {
  weekRange?: string;
  days?: ApiActivityDay[];
}

export interface TimelineState {
  start: number;
  end: number;
  state: DriverStateKey;
  duration: number;
  dddStatus: boolean;
}

export interface TimelineDay {
  date: string;
  dateObj: Dayjs;
  commitment: string;
  drivingTime: string;
  states: TimelineState[];
}

export interface DriverStateConfig {
  nameKey: string;
  color: string;
  striped?: boolean;
}

export const DRIVER_STATE_CONFIGS: Record<DriverStateKey, DriverStateConfig> = {
  DRIVING: { nameKey: "status_driving", color: "var(--color-green)" },
  DRIVING_WARNING: {
    nameKey: "status_driving_warning",
    color: "var(--color-orange)",
    striped: true,
  },
  DRIVING_VIOLATION: {
    nameKey: "status_driving_violation",
    color: "var(--color-red)",
    striped: true,
  },
  BREAK: { nameKey: "status_break", color: "var(--color-primary-shade)" },
  REST: { nameKey: "status_rest", color: "var(--color-blue)" },
  AVAILABLE: { nameKey: "status_available", color: "var(--color-yellow)" },
  WORK: { nameKey: "status_work", color: "var(--color-purple)" },
  NOT_AVAILABLE: {
    nameKey: "status_not_available",
    color: "var(--color-gray3)",
  },
};

export const mapActivityToStateKey = (
  activity: ApiDriverActivity = {},
): DriverStateKey => {
  const { workingState, stateType } = activity;
  const hasWarning = stateType?.includes("WARNING");
  const hasViolation = stateType?.includes("VIOLATION");

  if (hasViolation) return "DRIVING_VIOLATION";
  if (hasWarning) return "DRIVING_WARNING";
  if (workingState === "DRIVING") return "DRIVING";
  if (workingState === "NOT_AVAILABLE") return "NOT_AVAILABLE";
  if (stateType?.startsWith("BREAK_")) return "BREAK";
  if (workingState === "REST") return "REST";
  if (workingState === "DRIVER_AVAILABLE") return "AVAILABLE";
  return "WORK";
};

const timeToMinutes = (value?: string) => {
  const [hours = "0", minutes = "0"] = value?.split(":") ?? [];
  return Number.parseInt(hours, 10) * 60 + Number.parseInt(minutes, 10);
};

const calculateDrivingTime = (states: TimelineState[]) => {
  const totalMinutes = states
    .filter((state) => state.state.includes("DRIVING"))
    .reduce((total, state) => total + Math.max(0, state.duration), 0);
  return `${Math.floor(totalMinutes / 60)}h${String(totalMinutes % 60).padStart(2, "0")}m`;
};

export const transformActivityData = (
  activityData?: ActivityResponse,
  language = "en",
): TimelineDay[] => {
  if (!Array.isArray(activityData?.days)) return [];

  const year = activityData.weekRange?.match(/\d{4}$/)?.[0] ?? String(dayjs().year());
  const normalizedLanguage = language.split("-")[0] || "en";

  return activityData.days.map((day) => {
    const activityStates: TimelineState[] = (day.activities ?? [])
      .map((activity) => {
        const start = timeToMinutes(activity.startTime);
        const end = timeToMinutes(activity.endTime);
        return {
          start,
          end,
          state: mapActivityToStateKey(activity),
          duration: Math.max(0, end - start),
          dddStatus: Boolean(activity.dddStatus),
        };
      })
      .filter((state) => Number.isFinite(state.start) && state.end > state.start)
      .sort((left, right) => left.start - right.start);

    const states: TimelineState[] = [];
    let lastEnd = 0;
    activityStates.forEach((state) => {
      if (state.start > lastEnd) {
        states.push({
          start: lastEnd,
          end: state.start,
          state: "NOT_AVAILABLE",
          duration: state.start - lastEnd,
          dddStatus: false,
        });
      }
      states.push(state);
      lastEnd = Math.max(lastEnd, state.end);
    });
    if (lastEnd < 1440) {
      states.push({
        start: lastEnd,
        end: 1440,
        state: "NOT_AVAILABLE",
        duration: 1440 - lastEnd,
        dddStatus: false,
      });
    }

    const sourceDate = `${day.dateAndMonth ?? ""} ${year}`.trim();
    const parsedDate = dayjs(sourceDate);
    const dateObj = parsedDate.isValid() ? parsedDate.locale(normalizedLanguage) : dayjs();

    return {
      date: dateObj.format("YYYY-MM-DD"),
      dateObj,
      commitment: day.commitment || "0h00",
      drivingTime: calculateDrivingTime(states),
      states,
    };
  });
};

export const getMinutesFromDuration = (duration?: string) => {
  const match = duration?.match(/(\d+)h(?:(\d+))?/);
  if (!match) return 0;
  return Number.parseInt(match[1] ?? "0", 10) * 60 + Number.parseInt(match[2] ?? "0", 10);
};

export const getTotalDurationByState = (
  key: DriverStateKey,
  activityData?: ActivityResponse,
) => {
  const total = (activityData?.days ?? [])
    .flatMap((day) => day.activities ?? [])
    .filter((activity) => mapActivityToStateKey(activity) === key)
    .reduce((sum, activity) => sum + getMinutesFromDuration(activity.duration), 0);
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, "0")}m`;
};
